import { Capacitor, registerPlugin } from '@capacitor/core';
import { BodyAndContentType, SessionDescriptionHandler, SessionDescriptionHandlerFactory } from 'sip.js';
import { QualitySample, QualitySource } from './call-quality';

// Нативний WebRTC (iOS, NativeRtcPlugin.swift): мікрофон і звук розмови поза WKWebView
interface NativeRtcPlugin {
  create(options: { id: string; iceServers: string[] }): Promise<void>;
  createOffer(options: { id: string }): Promise<{ sdp: string }>;
  createAnswer(options: { id: string }): Promise<{ sdp: string }>;
  setRemoteDescription(options: { id: string; type: 'offer' | 'answer'; sdp: string }): Promise<void>;
  setMuted(options: { id: string; muted: boolean }): Promise<void>;
  close(options: { id: string }): Promise<void>;
  log(options: { message: string }): Promise<void>;
  getQuality(options: { id: string }): Promise<QualitySample>;
  setMaxBitrate(options: { id: string; bps: number }): Promise<void>;
}

const NativeRtc = registerPlugin<NativeRtcPlugin>('NativeRtc');

// WKWebView на iPhone відтворює WebRTC лише на гучномовець — там звук розмови веде нативна частина.
// Старі збірки без плагіна працюють як раніше (через WebView).
export const isNativeMediaAvailable = () => Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('NativeRtc');

// Діагностика дзвінків на iPhone: подія потрапляє в той самий файл, що й нативні (Documents/call-diag.log)
export const diagLog = (message: string): void => {
  if (!isNativeMediaAvailable()) return;
  NativeRtc.log({ message }).catch(() => {});
};

let nextId = 0;

/**
 * SessionDescriptionHandler для SIP.js, що замість RTCPeerConnection у WebView бере SDP з нативного з'єднання.
 * SIP-сигналізація лишається в SIP.js; без trickle ICE — як і стандартний обробник SIP.js.
 */
export class NativeSessionDescriptionHandler implements SessionDescriptionHandler {
  private readonly id = `rtc-${Date.now()}-${nextId++}`;
  private readonly ready: Promise<void>;
  // ми надіслали offer і чекаємо answer / нам надіслали offer і треба відповісти
  private offerSent = false;
  private answerNeeded = false;
  private closed = false;

  constructor(iceServers: string[]) {
    diagLog(`sdh ${this.id} create`);
    this.ready = NativeRtc.create({ id: this.id, iceServers });
  }

  async getDescription(): Promise<BodyAndContentType> {
    await this.ready;
    let sdp: string;
    diagLog(`sdh ${this.id} ${this.answerNeeded ? 'answer' : 'offer'}`);
    if (this.answerNeeded) {
      ({ sdp } = await NativeRtc.createAnswer({ id: this.id }));
      this.answerNeeded = false;
    } else {
      ({ sdp } = await NativeRtc.createOffer({ id: this.id }));
      this.offerSent = true;
    }
    return { body: sdp, contentType: 'application/sdp' };
  }

  async setDescription(sdp: string): Promise<void> {
    await this.ready;
    const type = this.offerSent ? 'answer' : 'offer';
    diagLog(`sdh ${this.id} remote ${type}`);
    await NativeRtc.setRemoteDescription({ id: this.id, type, sdp });
    this.offerSent = false;
    this.answerNeeded = type === 'offer';
  }

  hasDescription(contentType: string): boolean {
    return contentType === 'application/sdp';
  }

  sendDtmf(): boolean {
    return false;
  }

  setMuted(muted: boolean): void {
    this.ready.then(() => NativeRtc.setMuted({ id: this.id, muted })).catch(() => {});
  }

  // монітор якості зв'язку (call-quality.ts); старі нативні збірки без цих методів — без монітора
  qualitySource(): QualitySource {
    return {
      sample: () => this.ready.then(() => NativeRtc.getQuality({ id: this.id })).catch(() => null),
      setMaxBitrate: bps => this.ready.then(() => NativeRtc.setMaxBitrate({ id: this.id, bps: bps ?? 0 }))
    };
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    diagLog(`sdh ${this.id} close`);
    this.ready.finally(() => NativeRtc.close({ id: this.id }).catch(() => {}));
  }
}

export const nativeSessionDescriptionHandlerFactory = (iceServers: string[]): SessionDescriptionHandlerFactory =>
  () => new NativeSessionDescriptionHandler(iceServers);
